/**
 * Reading and writing the .pocketgolf course format.
 *
 * Pure functions over a plain course object, deliberately free of the DOM: the shape of the file is the
 * contract with the game, and a contract worth having is one a test can hold you to. `editor.js` keeps
 * the clicking and the rendering.
 *
 * The format has two generations, and which one a file is written in decides what it may carry:
 *
 * | | v1 | v2 |
 * |---|---|---|
 * | `grid` | yes | yes |
 * | `wind` | ignored | yes |
 * | `heights` (green contour) | ignored | yes |
 * | `elevation` (whole-hole terrain) | ignored | yes |
 * | `pinPositions` (alternate cups) | ignored | yes |
 *
 * v1 is what every course already in the world is. The game reads a missing `formatVersion` as v1 and
 * ignores v2-only fields on a v1 course, so writing them there would put data in a file that nothing will
 * ever read — which is why they are dropped rather than left in.
 */

import { DEFAULT_WIDTH, HEIGHT_RANGE, WIDTH_RANGE, gridWidth } from './grid.js';
import { hasContour, normaliseHeights } from './contour.js';
import { hasElevation, normaliseElevation } from './elevation.js';
import { cupCell, normalisePins } from './pins.js';

export const COURSE_TYPE = 'pocketgolf_course';
export const LATEST_FORMAT = 2;

/**
 * Which format a course object is in.
 *
 * Anything that is not an explicit 2 reads as v1 — including a missing field, which is what every course
 * written before Pocket Golf 2 has, and a nonsensical value, in keeping with a format whose reader has
 * never refused a file over a field it could reasonably guess.
 */
export function courseFormatOf(courseData) {
	return courseData && courseData.formatVersion === 2 ? 2 : 1;
}

export function isV2(courseData) {
	return courseFormatOf(courseData) >= 2;
}

/**
 * A course object as it should be written to disk.
 *
 * `formatVersion` is written at both versions rather than only at v2. The game reads a missing field as
 * v1, but a file that says which format it is can be checked by a reader that does not know the default —
 * and every shipped v1 course carries the number for exactly that reason.
 */
export function exportCourse(courseData) {
	const version = courseFormatOf(courseData);
	const exported = { ...courseData, type: COURSE_TYPE, formatVersion: version };

	if (version >= 2 && courseData.wind) {
		exported.wind = courseData.wind;
	} else {
		delete exported.wind;
	}

	exported.levels = (courseData.levels || []).map((level) => exportLevel(level, version));
	return exported;
}

/**
 * One hole, with its contour and its terrain written only when there is something to write.
 *
 * A dead-flat green is indistinguishable from no contour and the game treats it as none, so a matrix of
 * identical numbers is dropped rather than exported. The same goes for a hole at one uniform step. That
 * keeps a file from claiming a feature it does not use, and keeps the diff of a course nobody sculpted
 * completely empty.
 *
 * Both may be written on the same hole. The game reads `elevation` in preference and derives the green's
 * shape from it, so a hole with terrain does not need `heights` — but a hole may still carry one from
 * before it was sculpted, and dropping somebody's green contour because they raised a hillside would be
 * destroying work they never asked us to touch.
 */
function exportLevel(level, version) {
	const exported = { ...level };
	delete exported.heights;
	delete exported.elevation;
	delete exported.pinPositions;

	if (version >= 2 && hasContour(level.grid || [], level.heights || [])) {
		exported.heights = normaliseHeights(level.heights, level.grid);
	}
	if (version >= 2 && hasElevation(level.elevation || [])) {
		exported.elevation = normaliseElevation(level.elevation, level.grid);
	}
	// The extras only. The cup itself is the `0` already in the grid, and a hole whose every alternate has
	// been taken away is a hole with one cup again — so the field goes rather than being written empty.
	const pins = normalisePins(level.pinPositions, level.grid || []);
	if (version >= 2 && pins.length > 0) {
		exported.pinPositions = pins;
	}
	return exported;
}

/**
 * A course object as read from a file, with everything shaped so the editor can work on it.
 *
 * Forgiving in the same places the game's parser is: a missing version is v1, a missing `levels` is none,
 * and a heights matrix that is short or contains junk is padded with zeros rather than rejected. A file
 * that is wrong in a way the editor cannot guess at throws, because loading half a course silently is
 * worse than saying so.
 */
export function importCourse(parsed) {
	if (!parsed || typeof parsed !== 'object') {
		throw new Error('Invalid JSON object.');
	}
	if (parsed.type !== COURSE_TYPE) {
		throw new Error(`Invalid file type. Expected type "${COURSE_TYPE}".`);
	}

	const version = courseFormatOf(parsed);
	const course = { ...parsed, formatVersion: version };
	if (!Array.isArray(course.levels)) course.levels = [];
	if (version < 2) delete course.wind;

	course.levels = course.levels.map((level) => {
		const imported = { ...level };
		if (!Array.isArray(imported.grid) || typeof imported.grid[0] !== 'string') {
			imported.grid = [];
		}
		// Shaped to the grid on the way in, so a hand-edited matrix that is a few characters short loads
		// rather than painting a contour full of holes. Dropped outright on a v1 course, which cannot
		// carry one.
		if (version >= 2 && Array.isArray(imported.heights) && imported.grid.length > 0) {
			imported.heights = normaliseHeights(imported.heights, imported.grid);
		} else {
			delete imported.heights;
		}
		if (version >= 2 && Array.isArray(imported.elevation) && imported.grid.length > 0) {
			imported.elevation = normaliseElevation(imported.elevation, imported.grid);
		} else {
			delete imported.elevation;
		}
		// Same shaping on the way in as on the way out: coordinates off the grid, repeats and the cup
		// itself are dropped rather than carried, so what the editor draws is exactly what the game will
		// draw from. A v1 course cannot carry them at all.
		if (version >= 2) {
			const pins = normalisePins(imported.pinPositions, imported.grid);
			if (pins.length > 0) imported.pinPositions = pins;
			else delete imported.pinPositions;
		} else {
			delete imported.pinPositions;
		}
		return imported;
	});

	return course;
}

/**
 * What the game will refuse or misplay, per hole.
 *
 * Every one of these is cheap to check here and expensive to find by playing. A hole with no cup looks
 * perfectly fine in the grid; a hole with two is worse, because the game silently takes the first and the
 * other one is a flag the ball can never drop into.
 */
export function courseWarnings(courseData) {
	const warnings = [];
	const levels = courseData.levels || [];

	levels.forEach((level, index) => {
		const label = `Hole ${index + 1}`;
		const grid = level.grid || [];
		const flat = grid.join('');

		const tees = (flat.match(/X/g) || []).length;
		const cups = (flat.match(/0/g) || []).length;

		if (tees === 0) warnings.push(`${label}: no tee (paint one X).`);
		if (tees > 1) warnings.push(`${label}: ${tees} tees — the game takes the first.`);
		if (cups === 0) warnings.push(`${label}: no cup (paint one ⛳).`);
		if (cups > 1) warnings.push(`${label}: ${cups} cups — the game takes the first.`);

		if (grid.length === 0) warnings.push(`${label}: empty grid.`);
		// Ragged rows first: every width check below reads the first row, so a hole whose rows disagree
		// has to be reported as that rather than as whatever the first one happens to say.
		const width = gridWidth(grid);
		if (grid.some((row) => row.length !== width)) {
			warnings.push(`${label}: its rows are not all the same width (the first is ${width}).`);
		}
		// The game reads a v1 hole at exactly 20 columns whatever its rows say — padding what is short
		// and discarding what is long. A wide v1 hole is therefore not a wide hole, it is a hole the
		// editor draws one way and the game plays another.
		if (width > 0 && width !== DEFAULT_WIDTH && !isV2(courseData)) {
			warnings.push(
				`${label}: is ${width} wide, but a v1 course plays every hole at ${DEFAULT_WIDTH} — switch the course to v2.`);
		}
		if (width > WIDTH_RANGE.max) {
			warnings.push(`${label}: is ${width} wide; the game crops a hole to ${WIDTH_RANGE.max}.`);
		}
		if (grid.length > HEIGHT_RANGE.max) {
			warnings.push(
				`${label}: is ${grid.length} rows tall; the game drops everything past ${HEIGHT_RANGE.max}.`);
		}
		if (!(level.par >= 1)) warnings.push(`${label}: par must be at least 1.`);

		// Every pin is a place the flag may actually stand, so the same two things have to be true of it
		// that are true of the cup: it is on the putting surface, and it is somewhere the author can see.
		// A pin in the rough is not a hard hole, it is an unreachable one.
		const pins = normalisePins(level.pinPositions, grid);
		pins.forEach((pin) => {
			const char = (grid[pin.y] || '').charAt(pin.x);
			if (char !== 'G' && char !== '0') {
				warnings.push(`${label}: alternate pin at ${pin.x}, ${pin.y} is not on the green.`);
			}
		});
		if (pins.length > 0 && !isV2(courseData)) {
			warnings.push(`${label}: has alternate pins but the course is v1, so they are ignored.`);
		}
		if (pins.length > 0 && !cupCell(grid)) {
			warnings.push(`${label}: has alternate pins but no cup to alternate with.`);
		}

		// A contour on a hole with no green is data the game will never read.
		if (level.heights && !flat.includes('G') && !flat.includes('0')) {
			warnings.push(`${label}: has a slope but no green to apply it to.`);
		}
		// Both matrices on one hole: playable, but the green contour is the one that gets ignored, and an
		// author who painted it and cannot see it in the game deserves to be told why.
		if (level.heights && hasElevation(level.elevation || [])) {
			warnings.push(`${label}: has terrain, so its separate green slope is ignored — the green takes its shape from the terrain.`);
		}
	});

	if (levels.length === 0) warnings.push('The course has no holes.');
	if (!isV2(courseData) && courseData.wind) {
		warnings.push('Wind is set but the course is v1, so it is ignored.');
	}

	return warnings;
}
